import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatErr, fmtMoney } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import { Search, RefreshCw, Gift, Ban, Zap, FlaskConical, Unlock, Trash2, ShieldCheck, ArrowDownToLine, Check, AlertTriangle } from "lucide-react";
import EmergentLockCard from "@/components/EmergentLockCard";
import LaunchPanel from "@/components/LaunchPanel";
import ReferralPotCard from "@/components/ReferralPotCard";
import VaultPoolCard from "@/components/VaultPoolCard";

function StatusPill({ active, label, color = "rgb(var(--c-forest))" }) {
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider"
      style={active
        ? { background: color, color: "white" }
        : { background: "rgb(var(--c-sand-light))", color: "rgb(var(--c-sage))" }}>
      {label}
    </span>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({}); // {user_id: action}
  const [err, setErr] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [pendingDeposits, setPendingDeposits] = useState(null); // null | []
  const [reconcileBusy, setReconcileBusy] = useState(false);
  const [reconcileResult, setReconcileResult] = useState(null);
  const [recentCaptures, setRecentCaptures] = useState(null);
  const [showRecent, setShowRecent] = useState(false);

  const loadRecentCaptures = async () => {
    try {
      const { data } = await api.get("/admin/card-deposits/recent?limit=10");
      setRecentCaptures(data);
    } catch (e) {
      setErr(`recent captures: ${formatErr(e.response?.data?.detail) || e.message}`);
    }
  };

  const loadPendingDeposits = async () => {
    try {
      const { data } = await api.get("/admin/card-deposits/pending");
      setPendingDeposits(data);
    } catch (e) {
      setErr(`pending deposits: ${formatErr(e.response?.data?.detail) || e.message}`);
    }
  };

  const retryPendingDeposits = async () => {
    let pin = sessionStorage.getItem("admin_pin") || "";
    if (!pin) {
      pin = window.prompt("Enter Admin PIN to confirm this action:") || "";
      if (!pin) return;
      sessionStorage.setItem("admin_pin", pin);
    }
    setReconcileBusy(true); setErr(""); setReconcileResult(null);
    try {
      const { data } = await api.post("/admin/card-deposits/retry", {},
        { headers: { "X-Admin-PIN": pin } });
      setReconcileResult(data);
      await loadPendingDeposits();
    } catch (e) {
      if (e.response?.status === 401) sessionStorage.removeItem("admin_pin");
      setErr(`retry: ${formatErr(e.response?.data?.detail) || e.message}`);
    } finally {
      setReconcileBusy(false);
    }
  };

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get(`/admin/users${q ? `?q=${encodeURIComponent(q)}` : ""}`);
      setUsers(data);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (user?.is_admin) { load(); loadPendingDeposits(); }
  }, [user?.is_admin]);

  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;

  const doAction = async (uid, key, makeReq) => {
    setBusy((b) => ({ ...b, [uid]: key }));
    setErr("");
    try {
      let pin = sessionStorage.getItem("admin_pin") || "";
      if (!pin) {
        pin = window.prompt("Enter Admin PIN to confirm this action:") || "";
        if (!pin) { return; }
        sessionStorage.setItem("admin_pin", pin);
      }
      await makeReq({ headers: { "X-Admin-PIN": pin } });
      await load();
    } catch (e) {
      if (e.response?.status === 401 && /pin/i.test(e.response?.data?.detail || "")) {
        sessionStorage.removeItem("admin_pin");
      }
      setErr(`${key}: ${formatErr(e.response?.data?.detail) || e.message}`);
    } finally {
      setBusy((b) => { const c = { ...b }; delete c[uid]; return c; });
    }
  };

  const comp = (u) => doAction(u.id, "comp", (cfg) => api.post(`/admin/users/${u.id}/comp`, { paid: !u.paid }, cfg));
  const togglePM = (u) => doAction(u.id, "pm", (cfg) => api.post(`/admin/users/${u.id}/production-mode`, { production_mode: !u.production_mode }, cfg));
  const reset = (u) => doAction(u.id, "reset", (cfg) => api.post(`/admin/users/${u.id}/reset`, {}, cfg));
  const resetVaults = (u) => doAction(u.id, "resetVaults", (cfg) => api.post(`/admin/users/${u.id}/reset-vaults`, {}, cfg));
  const unlock = (u) => doAction(u.id, "unlock", (cfg) => api.post(`/admin/users/${u.id}/unlock`, {}, cfg));
  const remove = (u) => doAction(u.id, "delete", (cfg) => api.delete(`/admin/users/${u.id}`, cfg));

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString() : "—";

  return (
    <div className="min-h-screen bg-cream">
      <AppHeader />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-clay">Admin · Restricted</div>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-2">User console.</h1>
            <p className="text-moss mt-3 max-w-2xl">
              Comp / un-comp accounts, reset balances, toggle real-vs-demo timing, unlock locked logins.
            </p>
          </div>
          <a href="/admin/tickets" data-testid="admin-tickets-link"
            className="inline-flex items-center gap-2 bg-ink text-cream rounded-xl px-4 py-2.5 text-sm font-medium hover:bg-ink-deep transition-colors">
            Support tickets queue →
          </a>
          <a href="/admin/waitlist" data-testid="admin-waitlist-link"
            className="inline-flex items-center gap-2 bg-forest text-cream rounded-xl px-4 py-2.5 text-sm font-medium hover:bg-forest-deep transition-colors">
            Launch waitlist →
          </a>
          <a href="/admin/cash-out" data-testid="admin-cash-out-link"
            className="inline-flex items-center gap-2 bg-gold-dark text-cream rounded-xl px-4 py-2.5 text-sm font-medium hover:bg-[#6F5824] transition-colors">
            Cash out to debit card →
          </a>
        </div>

        <LaunchPanel />

        <VaultPoolCard />

        <ReferralPotCard />

        <EmergentLockCard />

        {/* Card-deposit reconciliation panel */}
        <div className="mt-8 bg-cream-warm border border-sand-warm rounded-2xl p-5"
             data-testid="admin-deposit-reconcile-panel">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-gold-dark/10 flex items-center justify-center shrink-0">
                <ArrowDownToLine className="w-5 h-5 text-gold-dark" strokeWidth={1.5} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">
                  Debit-card deposits · Reconciliation
                </div>
                <h3 className="font-serif text-xl mt-0.5">
                  {pendingDeposits === null
                    ? "Checking pending deposits…"
                    : pendingDeposits.length === 0
                      ? "All card deposits settled."
                      : `${pendingDeposits.length} pending deposit${pendingDeposits.length === 1 ? "" : "s"}`}
                </h3>
                <p className="text-xs text-moss mt-1 max-w-xl leading-relaxed">
                  Card deposits move user funds via PayPal → your bank pool → user&apos;s Spending.
                  If the pool was underfunded at capture time, the user-credit ACH is held as <strong>pending</strong>.
                  Fund the pool first, then click Retry.
                </p>
              </div>
            </div>
            <button onClick={retryPendingDeposits}
              disabled={reconcileBusy || (pendingDeposits && pendingDeposits.length === 0)}
              className="inline-flex items-center gap-2 bg-forest text-white rounded-lg px-4 py-2 text-xs font-medium hover:bg-forest-deep disabled:opacity-60"
              data-testid="admin-retry-deposits-btn">
              <RefreshCw className={`w-3.5 h-3.5 ${reconcileBusy ? "animate-spin" : ""}`} strokeWidth={1.5} />
              {reconcileBusy ? "Retrying…" : "Retry pool → user ACH"}
            </button>
          </div>

          {reconcileResult && (
            <div className="mt-4 p-3 rounded-lg text-xs bg-linen border border-sand"
                 data-testid="admin-reconcile-result">
              <div className="flex items-center gap-2 font-medium text-ink">
                {reconcileResult.still_pending === 0
                  ? <Check className="w-4 h-4 text-forest" />
                  : <AlertTriangle className="w-4 h-4 text-clay" />}
                Attempted {reconcileResult.attempted} · Settled {reconcileResult.settled} · Still pending {reconcileResult.still_pending}
              </div>
              {reconcileResult.items?.filter(i => !i.settled).slice(0, 3).map((i, idx) => (
                <div key={idx} className="mt-1 text-clay font-mono text-[11px] truncate">
                  ↳ ${i.amount?.toFixed(2)} · {i.error || "unknown error"}
                </div>
              ))}
            </div>
          )}

          {pendingDeposits && pendingDeposits.length > 0 && (
            <div className="mt-4 divide-y divide-sand-warm" data-testid="admin-pending-deposits-list">
              {pendingDeposits.slice(0, 10).map((d) => (
                <div key={d.id} className="py-2 flex items-center justify-between text-xs">
                  <div className="truncate">
                    <span className="font-mono text-ink">${(d.amount || 0).toFixed(2)}</span>
                    <span className="text-sage ml-3">{d.user_email}</span>
                  </div>
                  <div className="text-[10px] text-sage font-mono ml-3 shrink-0">
                    {d.created_at ? new Date(d.created_at).toLocaleDateString() : "—"}
                  </div>
                </div>
              ))}
              {pendingDeposits.length > 10 && (
                <div className="pt-2 text-[10px] text-sage">+{pendingDeposits.length - 10} more…</div>
              )}
            </div>
          )}

          {/* Recent captures debug */}
          <div className="mt-4 pt-3 border-t border-sand-warm">
            <button onClick={() => {
                const next = !showRecent;
                setShowRecent(next);
                if (next && recentCaptures === null) loadRecentCaptures();
              }}
              className="text-[11px] uppercase tracking-[0.18em] font-semibold text-sage hover:text-ink inline-flex items-center gap-1"
              data-testid="admin-recent-captures-toggle">
              {showRecent ? "▾" : "▸"} Recent capture diagnostics
            </button>
            {showRecent && (
              <div className="mt-3 space-y-2" data-testid="admin-recent-captures-list">
                {recentCaptures === null && <div className="text-xs text-sage">Loading…</div>}
                {recentCaptures?.length === 0 && (
                  <div className="text-xs text-sage">No captures yet.</div>
                )}
                {recentCaptures?.map((c) => (
                  <div key={c.order_id}
                       className="bg-white border border-sand rounded-lg p-3 text-[11px]">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="font-mono text-ink">
                        ${(c.amount || 0).toFixed(2)} · {c.user_email}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold ${
                          c.status === "COMPLETED" ? "bg-mint text-forest-deep" :
                          c.status === "REFUNDED_NON_DEBIT" ? "bg-clay-light text-clay" :
                          "bg-linen text-moss"
                        }`}>{c.status}</span>
                        {c.card_type && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold bg-ink text-white">
                            {c.card_type}
                          </span>
                        )}
                        {!c.card_type && c.paypal_wallet_email && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold bg-cream-warm border border-sand-warm text-gold-dark">
                            PayPal wallet
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="mt-1 text-[10px] text-sage font-mono">
                      {c.card_brand} {c.card_last4 ? `•••• ${c.card_last4}` : ""} · order {c.order_id?.slice(-10)} · {c.captured_at ? new Date(c.captured_at).toLocaleString() : "—"}
                    </div>
                    {c.refund_error && (
                      <div className="mt-1 text-[10px] text-clay">refund error: {c.refund_error}</div>
                    )}
                    <details className="mt-1">
                      <summary className="cursor-pointer text-[10px] text-sage hover:text-ink">raw payment_source</summary>
                      <pre className="mt-1 bg-ink text-mint rounded p-2 text-[10px] overflow-x-auto">
{JSON.stringify(c.raw_payment_source, null, 2)}
                      </pre>
                    </details>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-10 flex items-center gap-3" data-testid="admin-toolbar">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-sage" strokeWidth={1.5} />
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Search email or name…"
              className="w-full pl-9 pr-3 py-2.5 bg-linen border border-sand rounded-lg focus:ring-2 focus:ring-forest focus:border-transparent outline-none text-sm"
              data-testid="admin-search" />
          </div>
          <button onClick={load} disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-forest text-white rounded-lg text-sm font-medium hover:bg-forest-deep disabled:opacity-60 transition-colors"
            data-testid="admin-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} strokeWidth={1.5} />
            {loading ? "Loading…" : "Refresh"}
          </button>
          <div className="ml-auto text-xs text-sage">
            <span data-testid="admin-count">{users.length}</span> user{users.length !== 1 ? "s" : ""}
          </div>
        </div>

        {err && <div className="mt-4 text-sm text-clay bg-clay-light px-4 py-3 rounded-lg" data-testid="admin-error">{err}</div>}

        <div className="mt-6 bg-linen border border-sand rounded-2xl overflow-hidden" data-testid="admin-table">
          {users.length === 0 && !loading && (
            <div className="p-12 text-center text-sage" data-testid="admin-empty">No users match.</div>
          )}
          <div className="divide-y divide-sand">
            {users.map((u) => {
              const isLocked = u.locked_until && new Date(u.locked_until) > new Date();
              const b = busy[u.id];
              return (
                <div key={u.id} className="px-5 py-4 grid grid-cols-12 gap-3 items-center" data-testid={`admin-row-${u.id}`}>
                  <div className="col-span-12 md:col-span-4">
                    <div className="flex items-center gap-2">
                      <div className="font-serif text-lg text-ink truncate" data-testid={`row-email-${u.id}`}>{u.email}</div>
                      {u.is_admin && <ShieldCheck className="w-4 h-4 text-clay shrink-0" strokeWidth={2} />}
                    </div>
                    <div className="text-xs text-sage mt-0.5">
                      {u.name || "—"} · joined {fmtDate(u.created_at)}
                      {u.last_login_at && <> · last in {fmtDate(u.last_login_at)}</>}
                    </div>
                  </div>

                  <div className="col-span-6 md:col-span-3 flex flex-wrap gap-1.5">
                    <StatusPill active={u.paid} label="paid" color="rgb(var(--c-forest))" />
                    {u.comped && <StatusPill active label="comp" color="rgb(var(--c-gold))" />}
                    {u.production_mode && <StatusPill active label="real" color="rgb(var(--c-ink))" />}
                    {u.two_factor_enabled && <StatusPill active label="2FA" color="rgb(var(--c-forest))" />}
                    {isLocked && <StatusPill active label="locked" color="rgb(var(--c-clay))" />}
                  </div>

                  <div className="col-span-6 md:col-span-2 text-right md:text-left">
                    <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Vault total</div>
                    <div className="font-mono text-base text-ink" data-testid={`row-total-${u.id}`}>{fmtMoney(u.vault_total)}</div>
                  </div>

                  <div className="col-span-12 md:col-span-3 flex flex-wrap gap-1.5 justify-end">
                    <button onClick={() => comp(u)} disabled={!!b || u.is_admin}
                      title={u.paid ? "Un-comp (remove paid)" : "Comp (give lifetime free)"}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium border transition-colors disabled:opacity-40 ${
                        u.paid ? "border-sand text-clay hover:bg-clay-light" : "border-forest text-forest hover:bg-forest hover:text-white"
                      }`}
                      data-testid={`btn-comp-${u.id}`}>
                      {u.paid ? <Ban className="w-3 h-3" strokeWidth={2} /> : <Gift className="w-3 h-3" strokeWidth={2} />}
                      {b === "comp" ? "…" : (u.paid ? "Un-comp" : "Comp")}
                    </button>

                    <button onClick={() => togglePM(u)} disabled={!!b}
                      title={u.production_mode ? "Switch to DEMO timing (30s holds)" : "Switch to REAL timing (10 days)"}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium border transition-colors disabled:opacity-40 ${
                        u.production_mode ? "border-ink text-ink hover:bg-ink hover:text-white" : "border-sand text-moss hover:border-ink"
                      }`}
                      data-testid={`btn-pm-${u.id}`}>
                      {u.production_mode ? <Zap className="w-3 h-3" strokeWidth={2} /> : <FlaskConical className="w-3 h-3" strokeWidth={2} />}
                      {b === "pm" ? "…" : (u.production_mode ? "Real" : "Demo")}
                    </button>

                    <button onClick={() => resetVaults(u)} disabled={!!b}
                      title="Zero vault balances + clear time-locks (keeps banking + cards)"
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium border border-sand text-moss hover:border-forest hover:text-forest disabled:opacity-40 transition-colors"
                      data-testid={`btn-reset-vaults-${u.id}`}>
                      <RefreshCw className="w-3 h-3" strokeWidth={2} />
                      {b === "resetVaults" ? "…" : "Reset vaults"}
                    </button>

                    <button onClick={() => reset(u)} disabled={!!b}
                      title="FULL reset: zero balances + delete banking, cards, and all activity"
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium border border-clay text-clay hover:bg-clay hover:text-white disabled:opacity-40 transition-colors"
                      data-testid={`btn-reset-${u.id}`}>
                      <RefreshCw className="w-3 h-3" strokeWidth={2} />
                      {b === "reset" ? "…" : "Full reset"}
                    </button>

                    {isLocked && (
                      <button onClick={() => unlock(u)} disabled={!!b}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium border border-gold text-gold hover:bg-gold hover:text-white transition-colors disabled:opacity-40"
                        data-testid={`btn-unlock-${u.id}`}>
                        <Unlock className="w-3 h-3" strokeWidth={2} />
                        {b === "unlock" ? "…" : "Unlock"}
                      </button>
                    )}

                    <button onClick={() => setConfirmDelete(u)} disabled={!!b || u.is_admin}
                      title="Delete user permanently"
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium border border-sand text-clay hover:bg-clay hover:text-white hover:border-clay disabled:opacity-40 transition-colors"
                      data-testid={`btn-delete-${u.id}`}>
                      <Trash2 className="w-3 h-3" strokeWidth={2} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {confirmDelete && (
        <div className="fixed inset-0 bg-ink/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="delete-confirm">
          <div className="bg-cream rounded-2xl border border-sand max-w-md w-full p-8">
            <h3 className="font-serif text-2xl">Delete {confirmDelete.email}?</h3>
            <p className="text-sm text-moss mt-3 leading-relaxed">
              This permanently removes the user, all vaults, transactions, time-locks, beneficiaries,
              banking, net worth, and budget data. This cannot be undone.
            </p>
            <div className="mt-6 flex gap-3">
              <button onClick={() => setConfirmDelete(null)}
                className="flex-1 px-4 py-2.5 rounded-lg border border-sand text-moss hover:bg-linen text-sm font-medium"
                data-testid="delete-cancel">Cancel</button>
              <button onClick={() => { remove(confirmDelete); setConfirmDelete(null); }}
                className="flex-1 px-4 py-2.5 bg-clay text-white rounded-lg hover:bg-[#9F4D3B] text-sm font-medium"
                data-testid="delete-confirm-btn">Delete forever</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
